import { Download, Copy, Edit2, ThumbsUp, ThumbsDown, RefreshCw, Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { models } from '../../ModelDropdown';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import ModelSelectorGrid from '../../ModelSelectorGrid';
import ActionButton from './ActionButton';

interface Message {
  imageId?: string;
  image?: string;
  model?: string;
  userPrompt?: string;
  promptId?: string;
  rating?: 'thumbs_up' | 'thumbs_down' | null;
}

interface ImageActionsProps {
  message: Message;
  sessionTitle?: string;
  onRegenerate?: (model: string, prompt: string, promptId?: string, modelObject?: any) => void;
  onEdit?: () => void;
  isGenerating?: boolean;
}

export default function ImageActions({ 
  message, 
  sessionTitle, 
  onRegenerate,
  onEdit,
  isGenerating = false
}: ImageActionsProps) {
  const { imageId, image, model, userPrompt, promptId, rating: initialRating } = message;
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  const [rating, setRating] = useState<'thumbs_up' | 'thumbs_down' | null>(initialRating || null);
  const [isUpdatingRating, setIsUpdatingRating] = useState(false);

  const currentModel = models.find(m => m.id === model) || models[0];

  // Update rating when prop changes
  useEffect(() => {
    setRating(initialRating || null);
  }, [initialRating]);

  const handleDownload = async () => {
    try {
      let svgContent = '';
      let filename = '';
      
      if (imageId) {
        const response = await fetch(`https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/${imageId}/orig`);
        svgContent = await response.text();
        filename = `${sessionTitle || 'svg'}-${imageId.substring(0, 8)}.svg`;
      } else if (image) {
        svgContent = image;
        filename = `${sessionTitle || 'generated'}.svg`;
      }
      
      if (svgContent) {
        const blob = new Blob([svgContent], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download:', error);
    }
  };

  const handleCopy = async () => {
    try {
      setIsCopying(true);
      if (imageId) {
        const response = await fetch(`https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/${imageId}/orig`);
        const text = await response.text();
        await navigator.clipboard.writeText(text);
      } else if (image) {
        await navigator.clipboard.writeText(image);
      }
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    } finally {
      setIsCopying(false);
    }
  };

  const handleEdit = () => {
    onEdit?.();
  };

  const handleModelSelect = (selectedModel: any) => {
    setIsModelSelectorOpen(false);
    if (userPrompt && onRegenerate) {
      onRegenerate(selectedModel.id, userPrompt, promptId, selectedModel);
    }
  };

  const handleRating = async (newRating: 'thumbs_up' | 'thumbs_down') => {
    if (!promptId || isUpdatingRating) return;
    
    const ratingToSet = rating === newRating ? null : newRating;
    
    // Optimistically update the UI immediately
    setRating(ratingToSet);
    setIsUpdatingRating(true);
    
    try {
      const response = await fetch('/api/prompts/rating', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promptId, rating: ratingToSet }),
      });
      
      if (!response.ok) {
        // Revert on failure
        setRating(rating);
        throw new Error('Failed to update rating');
      }
    } catch (error) {
      console.error('Error updating rating:', error);
    } finally {
      setIsUpdatingRating(false);
    }
  };

  // Don't show actions during generation
  if (isGenerating) return null;

  return (
    <div className="flex items-center gap-1 mt-1">
      <ActionButton onClick={handleDownload} icon={Download} label="Download" />
      
      <button
        onClick={handleCopy}
        disabled={isCopying}
        className={`flex items-center gap-1 group p-1.5 rounded-lg overflow-hidden hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${copiedToClipboard ? 'bg-green-50 dark:bg-green-900/30' : ''}`}
        title={copiedToClipboard ? 'Copied!' : 'Copy'}
      >
        {isCopying ? (
          <Loader2 className="h-4 w-4 text-gray-500 dark:text-gray-400 animate-spin" />
        ) : (
          <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
        )}
        {copiedToClipboard && <span className="text-gray-700 dark:text-gray-300 hidden group-hover:block text-xs">Copied!</span>}
      </button>
      
      <ActionButton className="hidden sm:flex" onClick={handleEdit} icon={Edit2} label="Edit" />
      
      {/* Model switch button with popover */}
      {userPrompt && (
        <Popover open={isModelSelectorOpen} onOpenChange={setIsModelSelectorOpen}>
          <PopoverTrigger asChild>
            <button
              className="flex items-center gap-1 group p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={`Regenerate`}
            >
              <RefreshCw className="h-4 w-4 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
              {!isModelSelectorOpen && (
                <span className="text-gray-700 dark:text-gray-300 hidden group-hover:block text-xs">{currentModel.name}</span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[520px] p-0 overflow-hidden border-gray-200 dark:border-gray-700" align="start" sideOffset={8}>
            <ModelSelectorGrid
              models={models}
              selectedModel={currentModel}
              onModelSelect={handleModelSelect}
              onClose={() => setIsModelSelectorOpen(false)}
            />
          </PopoverContent>
        </Popover>
      )}

      <div className="ml-auto flex items-center gap-1">
        <button
          onClick={() => handleRating('thumbs_up')}
          disabled={isUpdatingRating}
          className={`flex items-center gap-1 group p-1.5 rounded-lg overflow-hidden hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${rating === 'thumbs_up' ? 'bg-green-50 dark:bg-green-900/30' : ''}`}
          title="Good result"
        >
          <ThumbsUp className={`h-4 w-4 transition-colors ${rating === 'thumbs_up' ? 'text-green-600 dark:text-green-400 fill-green-600 dark:fill-green-400' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200'}`} />
        </button>
        <button
          onClick={() => handleRating('thumbs_down')}
          disabled={isUpdatingRating}
          className={`flex items-center gap-1 group p-1.5 rounded-lg overflow-hidden hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${rating === 'thumbs_down' ? 'bg-red-50 dark:bg-red-900/30' : ''}`}
          title="Poor result"
        >
          <ThumbsDown className={`h-4 w-4 transition-colors ${rating === 'thumbs_down' ? 'text-red-600 dark:text-red-400 fill-red-600 dark:fill-red-400' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200'}`} />
        </button>
      </div>
    </div>
  );
}